import { ButtonGroup } from '@toggle/design-system';
import { Entity } from '@toggle/toggle';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMarketReactionData } from '~/hooks/use-market-reaction-data/useMarketReactionData';
import { MarketReactionChart } from '~/modules/market-reaction-charts/MarketReactionChart';
import { useGetEarningById } from '~/react-query-hooks/earnings/useGetEarningById';

import { HistoricalReaction } from './historical-reaction/HistoricalReaction';
import * as S from './MarketReactionSection.styles';

interface MarketReactionSectionProps {
  entity: Entity;
  earningsId: string;
  reportingDate: string;
}

export const MarketReactionSection = ({
  entity,
  earningsId,
  reportingDate,
}: MarketReactionSectionProps) => {
  const { t } = useTranslation(['earnings']);
  const buttonOptions = useMemo(
    () => [
      t('earnings:earningsInsight.drawer.earningsRecap.marketReaction'),
      t('earnings:earningsInsight.drawer.earningsRecap.historicalReaction'),
    ],
    [t]
  );
  const [selectedButtonIndex, setSelectedButtonIndex] = useState(0);
  const isMarketReactionSelected = selectedButtonIndex === 0;

  const { data: earningsData, isLoading } = useGetEarningById(earningsId);

  const { priceChangeData, fromDateEST, toDateEST } = useMarketReactionData({
    ticker: entity?.subscribable_ticker ?? '',
    reportingDate,
  });

  const hasEpsData = !isLoading && earningsData?.eps?.surprise !== undefined;

  return (
    <S.Root>
      {hasEpsData ? (
        <>
          <S.StyledButtonGroup>
            <ButtonGroup
              labels={buttonOptions}
              activeIndex={selectedButtonIndex}
              onClick={setSelectedButtonIndex}
            />
          </S.StyledButtonGroup>
          {!isMarketReactionSelected && (
            <HistoricalReaction entity={entity} earningsId={earningsId} />
          )}
        </>
      ) : (
        <S.Title>
          {t(
            'earnings:earningsInsight.drawer.earningsRecap.items.marketReaction'
          )}
        </S.Title>
      )}
      {(!hasEpsData || isMarketReactionSelected) && (
        <MarketReactionChart
          fromDateEST={fromDateEST}
          toDateEST={toDateEST}
          lines={priceChangeData ? [priceChangeData] : []}
        />
      )}
    </S.Root>
  );
};
